import { useState } from "react";

import { formatMultiplier, formatPaws, hasPremiumPerks, maxMultiPlay, parsePaws, type PlinkoDropDto } from "@koshkodrop/shared";

import { GameShell } from "../components/GameShell";
import { PlinkoBoard } from "../games/plinko/PlinkoBoard";
import { ApiError, api } from "../lib/api";
import { haptic } from "../lib/telegram";
import { useSession } from "../store/session";

interface Props {
  onBack: () => void;
}

const QUICK = ["50", "100", "200", "500"];

export function PlinkoPage({ onBack }: Props) {
  const { balance, setBalance, user } = useSession();
  const [stakeText, setStakeText] = useState("50");
  const [count, setCount] = useState(1);
  const [path, setPath] = useState<number[] | null>(null);
  const [bucket, setBucket] = useState<number | null>(null);
  const [running, setRunning] = useState(false);
  const [payout, setPayout] = useState<string | null>(null);
  const [multiplier, setMultiplier] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [extraDrops, setExtraDrops] = useState<PlinkoDropDto[] | null>(null);

  const maxCount = maxMultiPlay(user?.group ?? "user");
  const premium = hasPremiumPerks(user?.group ?? "user");
  const pack = Math.min(count, maxCount);

  const drop = async () => {
    if (running) return;
    let stake: bigint;
    try {
      stake = parsePaws(stakeText.replace(",", ".").trim() || "0");
    } catch {
      haptic.error();
      return;
    }
    if (stake <= 0n) return;
    if (stake * BigInt(pack) > balance) {
      haptic.error();
      setError("Не хватает лапок");
      return;
    }

    setError(null);
    setPayout(null);
    try {
      const data = await api.dropPlinko(stake.toString(), pack);
      const drops = data.drops ?? [data.drop];
      const last = drops[drops.length - 1] ?? data.drop;
      setPath(last.path);
      setBucket(last.bucket);
      const totalPayout = drops.reduce((sum, item) => sum + BigInt(item.payout), 0n);
      setPayout(totalPayout.toString());
      setMultiplier(last.multiplier);
      setExtraDrops(drops.length > 1 ? drops : null);
      setBalance(BigInt(data.balance));
      setRunning(true);
      haptic.tap("medium");
    } catch (caught) {
      haptic.error();
      setError(caught instanceof ApiError ? caught.message : "Не удалось бросить");
    }
  };

  return (
    <GameShell title="Плинко" subtitle="Крайние корзины редкие и жирные, ближе к центру чаще 1.2–1.5x." onBack={onBack}>
      <PlinkoBoard
        path={path}
        bucket={running ? null : bucket}
        running={running}
        onDone={() => {
          setRunning(false);
          if (payout && BigInt(payout) > 0n) haptic.success();
        }}
      />

      <div className="flex items-center gap-2 rounded-2xl bg-night-850 px-3 py-2.5">
        <input
          value={stakeText}
          onChange={(event) => setStakeText(event.target.value.replace(/[^\d.,]/g, ""))}
          inputMode="decimal"
          className="tabular w-full bg-transparent text-lg font-bold text-white outline-none"
        />
        <span className="text-paws-400">🐾</span>
      </div>
      <div className="flex gap-2">
        {QUICK.map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => {
              haptic.select();
              setStakeText(value);
            }}
            className={`flex-1 rounded-xl py-2 text-sm font-bold ${
              stakeText === value ? "bg-paws-500 text-night-950" : "bg-night-850 text-white/55"
            }`}
          >
            {value}
          </button>
        ))}
      </div>

      <div>
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wide text-white/35">
          {premium ? "Сколько бросков" : "Пачка бросков · Premium"}
        </p>
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((value) => {
            const locked = value > maxCount;
            return (
              <button
                key={value}
                type="button"
                onClick={() => {
                  if (locked) {
                    haptic.error();
                    setError("x5 бросков — для Premium");
                    return;
                  }
                  haptic.select();
                  setCount(value);
                }}
                className={`flex-1 rounded-xl py-2 text-sm font-bold ${
                  pack === value && !locked
                    ? "bg-paws-500 text-night-950"
                    : locked
                      ? "bg-night-850 text-white/25"
                      : "bg-night-850 text-white/55"
                }`}
              >
                {value}
              </button>
            );
          })}
        </div>
      </div>

      {!running && payout !== null && multiplier !== null && (
        <div
          className={`rounded-2xl px-4 py-3 text-center text-sm font-extrabold ${
            BigInt(payout) > 0n ? "bg-win-500/15 text-win-400" : "bg-night-850 text-white/45"
          }`}
        >
          {extraDrops ? (
            <p>
              {extraDrops.length} броска · +{formatPaws(BigInt(payout))} 🐾
            </p>
          ) : (
            <p>
              {formatMultiplier(multiplier)} · {BigInt(payout) > 0n ? `+${formatPaws(BigInt(payout))}` : "0"} 🐾
            </p>
          )}
          {extraDrops && (
            <p className="mt-1 text-[11px] font-bold text-white/45">
              {extraDrops.map((item) => formatMultiplier(item.multiplier)).join("  ")}
            </p>
          )}
        </div>
      )}

      {error && <p className="text-center text-sm font-semibold text-lose-400">{error}</p>}

      <button
        type="button"
        onClick={() => void drop()}
        disabled={running}
        className="w-full rounded-2xl bg-paws-500 py-4 text-lg font-extrabold text-night-950 active:scale-[0.98] disabled:opacity-35"
      >
        {running ? "Скачет…" : pack > 1 ? `Бросить ×${pack}` : "Бросить котика"}
      </button>
    </GameShell>
  );
}
